import { useEffect, useState } from "react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ObsChartPicker } from "./ObsChartPicker";
import {
  useObsChartOptions,
  useUpdateObsReport,
} from "@/features/obs/hooks/useObsQueries";
import {
  chartsComplete,
  dimHeadersFromMapping,
} from "@/features/obs/lib/chart-labels";
import { handleApiError } from "@/lib/error-handler";
import type { ObsCardSpec, ObsReport } from "@/types/obs";

/**
 * Change which charts an existing report draws.
 *
 * Without this the picker would be a one-shot decision taken during creation,
 * before anybody has seen the report rendered — and the first thing an operator
 * does after seeing their charts is want to change them.
 *
 * The offers are re-fetched rather than remembered: a later upload can change the
 * data's shape, so a chart that was drawable when the report was created may not
 * be now. Asking the server each time is what keeps a stale choice from silently
 * publishing a blank card.
 *
 * 🔄 Open for the six, and saving no longer moves them anywhere. Until 2026-09-11
 * a saved spec switched one of the six onto the generic renderer, which redrew
 * its KPI row and grouped its detail table — a client hit exactly that. Now the
 * screen keeps drawing its own cards and only their ORDER is data, so the editor
 * lists them as fixed entries the operator can move, hide or widen.
 */
export function ObsEditChartsDialog({
  report,
  open,
  onOpenChange,
}: {
  report: ObsReport;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [charts, setCharts] = useState<ObsCardSpec[]>([]);
  const options = useObsChartOptions(report.slug, open);
  const update = useUpdateObsReport();

  // Reload from the server whenever it reopens, so an abandoned edit is not
  // shown back as if it had been saved.
  useEffect(() => {
    if (!open) return;
    // Saved spec first; failing that, whatever the report already DRAWS. For one
    // of the six that second branch is its bespoke layout, so the editor opens
    // showing the charts on the page in their real order rather than an empty
    // list the operator has to reconstruct. Created reports get [] as before.
    const saved = (options.data?.charts ??
      report.charts ??
      []) as ObsCardSpec[];
    setCharts(
      saved.length ? saved : ((options.data?.suggested ?? []) as ObsCardSpec[]),
    );
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, options.data]);

  const headers = dimHeadersFromMapping(
    (report.mapping?.sheets?.[0]?.roles ?? {}) as Partial<
      Record<string, string>
    >,
    (report.mapping?.sheets?.[0]?.enRoles ?? {}) as Partial<
      Record<string, string>
    >,
  );

  const save = async () => {
    try {
      await update.mutateAsync({
        slug: report.slug,
        // An empty list means "choose for me": the composed screen falls back to
        // a sensible default drawn from the data rather than rendering nothing.
        patch: { charts: charts.length ? charts : null },
      });
      onOpenChange(false);
    } catch (e) {
      handleApiError(e);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>رسوم التقرير — {report.title.ar}</DialogTitle>
          <DialogDescription>
            اختر الرسوم وترتيبها وعناوينها. تُعرض فقط الأنواع التي تدعمها
            البيانات المنشورة حاليًا.
          </DialogDescription>
        </DialogHeader>

        {options.isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader /> جارٍ حساب الرسوم المتاحة…
          </p>
        )}

        {report.isDeclared && (
          <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm">
            <div className="font-semibold">هذا أحد التقارير الستة الأصلية</div>
            <p className="mt-1 text-muted-foreground">
              البطاقات المعلَّمة «بطاقة أصلية» من تصميم الشاشة نفسها. يمكنك
              ترتيبها أو إخفاؤها أو تغيير عرضها، ويمكنك إضافة رسوم جديدة بينها —
              أما محتواها فتبقى الشاشة هي التي ترسمه، فلا يتغيّر شكلها.
            </p>
            <p className="mt-2 text-muted-foreground">
              صفّ المؤشرات العلوي يخصّ هذه الشاشة أيضًا ولا يتأثر بما تختاره
              هنا.
            </p>
          </div>
        )}

        {options.data && !options.data.hasData && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
            لا توجد بيانات منشورة لهذا التقرير بعد، فلا يمكن تحديد الرسوم
            المتاحة. ارفع ملفًا أولًا.
          </div>
        )}

        {options.data?.capabilities && (
          <ObsChartPicker
            options={options.data.options}
            capabilities={options.data.capabilities}
            headers={headers}
            charts={charts}
            builtins={options.data.builtins ?? []}
            onChange={setCharts}
          />
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={update.isPending}
          >
            إلغاء
          </Button>
          <Button
            onClick={save}
            disabled={
              update.isPending ||
              !options.data?.hasData ||
              !chartsComplete(charts)
            }
          >
            {update.isPending ? <Loader /> : null}
            حفظ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
